import { catchError, concatMap, EMPTY, lastValueFrom, map, Observable, Subject } from "rxjs";
import { ajax, AjaxResponse, type AjaxConfig } from "rxjs/ajax";
import { createContext, createResource, useContext, type Resource } from "solid-js";

import type {
    UtdoKvp,
    UtdoContact,
    UtdoDate,
    UtdoDisk,
    UtdoFile,
    UtdoLyrics,
    UtdoTrack,
    UtdoVideo,
} from "../types";

type Method =
    | "DELETE"
    | "GET"
    | "PATCH"
    | "POST"
    | "PUT"
    ;

export type ApiResponse<T> = {
    success?: T;
    error?: string;
};

export class Api {
    private _logged: Resource<boolean>;
    private _refetch: () => void;
    private _requestSubject$ = new Subject<Observable<any>>();
    constructor() {
        const [ logged, { refetch } ] = createResource(
            async () => {
                try {
                    return await lastValueFrom(this.getLogin());
                } catch (err: any) {
                    console.log(err?.error ?? err);
                    return false;
                }
            },
            { initialValue: false },
        )
        this._logged = logged;
        this._refetch = refetch;
        this._requestSubject$
            .pipe(concatMap((obs) => obs.pipe(catchError(() => EMPTY))))
            .subscribe();
    }

    fetch<T>(config: AjaxConfig) {
        return new Observable<AjaxResponse<T>>((obs) => {
            return ajax<T>(config)
            .subscribe({
                next: (res) => {
                    if (res.status === 401) {
                        this.checkIfLogged();
                    }
                    obs.next(res);
                },
                error: (err) => obs.error(err),
                complete: () => obs.complete(),
            });
        });
    }

    fetchApi<T>(method: Method, url: string, body?: any) {
        return this.fetch<ApiResponse<T>>({
            url,
            method,
            body: JSON.stringify(body),
            headers: {
                "Content-Type": "application/json",
            },
            responseType: "json",
            withCredentials: true,
        }).pipe(
            map((val) => {
                const res = val.response;
                if (res.error) {
                    throw new Error(res.error);
                }
                if (res.success === undefined) {
                    throw new Error("no success");
                }
                return res.success;
            })
        );
    }

    get logged() {
        return this._logged;
    }

    checkIfLogged() {
        this._refetch();
    }
    
    async disconnect() {
        await lastValueFrom(this.fetchApi<boolean>("DELETE", "/api/"));
        this.checkIfLogged();
        return;
    }

    getLogin() {
        return this.fetchApi<boolean>("GET", "/api/");
    }

    postLogin(pass: string) {
        return this.fetchApi<boolean>("POST", "/api/", pass);
    }

    selectDates() {
        return this.fetchApi<UtdoDate[]>("GET", "/api/dates/");
    }

    deleteDate(id: number) {
        return this.fetchApi<boolean>("DELETE", `/api/dates/${id}`);
    }

    insertDate(d: UtdoDate) {
        return this.fetchApi<boolean>("POST", "/api/dates/", d);
    }

    updateDate(d: UtdoDate) {
        return this.fetchApi<boolean>("PUT", `/api/dates/${d.id}`, d);
    }

    toggleDateVisibility(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/dates/${id}/visibility`);
    }

    deleteVideo(id: number) {
        return this.fetchApi<boolean>("DELETE", `/api/videos/${id}`);
    }

    insertVideo(v: UtdoVideo) {
        return this.fetchApi<number>("POST", "/api/videos/", v);
    }

    moveVideoDown(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/videos/${id}/down`)
    }

    moveVideoUp(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/videos/${id}/up`)
    }

    selectVideos() {
        return this.fetchApi<UtdoVideo[]>("GET", "/api/videos/");
    }

    toggleVideoVisibility(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/videos/${id}/visibility`);
    }

    updateVideo(v: UtdoVideo) {
        return this.fetchApi<boolean>("PUT", `/api/videos/${v.id}`, v);
    }

    getKvp(key: string) {
        return this.fetchApi<UtdoKvp[]>("GET", `/api/kvps/${key}`);
    }

    setKvp(key: string, value: string) {
        return this.fetchApi<boolean>("POST", `/api/kvps/${key}`, value);
    }

    deleteContact(id: number) {
        return this.fetchApi<boolean>("DELETE", `/api/contacts/${id}`);
    }

    insertContact(c: UtdoContact) {
        return this.fetchApi<boolean>("POST", "/api/contacts/", c);
    }

    moveContactDown(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/contacts/${id}/down`)
    }

    moveContactUp(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/contacts/${id}/up`)
    }

    selectContacts() {
        return this.fetchApi<UtdoContact[]>("GET", "/api/contacts/");
    }

    toggleContactVisibility(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/contacts/${id}/visibility`);
    }

    updateContact(v: UtdoContact) {
        return this.fetchApi<boolean>("PUT", `/api/contacts/${v.id}`, v);
    }

    deleteLyrics(id: number) {
        return this.fetchApi<boolean>("DELETE", `/api/songs/${id}`);
    }

    insertLyrics(c: UtdoLyrics) {
        return this.fetchApi<UtdoLyrics[]>("POST", "/api/songs/", c);
    }

    selectLyrics() {
        return this.fetchApi<UtdoLyrics[]>("GET", "/api/songs/");
    }

    updateLyrics(c: UtdoLyrics) {
        return this.fetchApi<boolean>("PUT", `/api/songs/${c.id}`, c);
    }

    deleteDisk(id: number) {
        return this.fetchApi<boolean>("DELETE", `/api/disks/${id}`);
    }

    insertDisk(disk: UtdoDisk) {
        return this.fetchApi<boolean>("POST", "/api/disks/", disk);
    }

    selectDisk() {
        return this.fetchApi<UtdoDisk[]>("GET", "/api/disks/");
    }

    updateDisk(disk: UtdoDisk) {
        return this.fetchApi<boolean>("PUT", `/api/disks/${disk.id}`, disk);
    }

    toggleDiskVisibility(id: number) {
        return this.fetchApi<boolean>("PATCH", `/api/disks/${id}/visibility`);
    }

    selectTracks() {
        return this.fetchApi<UtdoTrack[]>("GET", "/api/tracks/");
    }

    deleteTrack(id: number) {
        return this.fetchApi<boolean>("DELETE", `/api/tracks/${id}`);
    }

    insertTrack(track: UtdoTrack) {
        return this.fetchApi<boolean>("POST", "/api/tracks/", track);
    }

    updateTrack(track: UtdoTrack) {
        return this.fetchApi<boolean>("PUT", `/api/tracks/${track.id}`, track);
    }

    selectFiles(dir: string) {
        return this.fetchApi<UtdoFile[]>("GET", `/api/files/${dir}/`);
    }

    deleteFile(dir: string, name: string) {
        return this.fetchApi<boolean>("DELETE", `/api/files/${dir}/${name}`);
    }

    renameFile(dir: string, name: string, next: string) {
        return this.fetchApi<boolean>("PATCH", `/api/files/${dir}/${name}`, next);
    }
}
export const ApiContext = createContext<Api>();

export const useApi = () => {
    return useContext(ApiContext)!;
}